import { observer } from 'mobx-react-lite';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import camelRating from '~/assets/img/dubaiexpress/camel-rating.svg';
import { company } from '~/company/Company';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { CartSmallIcon, HeartIcon } from '~/components/MarketplaceIcons';
import StarIcon from '~/components/MarketplaceIcons/StarIcon';
import { PriceText } from '~/components/Price/PriceText';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import {
  useProductListAnalyticsContext,
  useProductSnippetAnalytics,
} from '~/components/ProductListAnalytics';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useMounted } from '~/hooks/useMounted';
import { cartDrawerStore } from '~/pages/DubaiExpress/components/CartDrawerDE/cartDrawerStore';
import { cartStore, mainStore, userStore } from '~/stores';
import type { Product } from '~/stores/CategoriesStore/Product';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';

import { NotifyBell } from './NotifyBell';

interface Props {
  product: Product;
  index: number;
  /** Border color of the card; collections on a tinted plate pass the plate color. */
  borderColor?: string;
  /** Dims the card and blocks its controls, e.g. for a draft product in favorites. */
  disabled?: boolean;
}

const CARD_IMAGE_WIDTH = 400;

/**
 * Compact product card of the marketplace grids (home collections, catalog,
 * product page recommendations, search results, favorites): square photo with a heart for favorites,
 * camel rating, two-line name, price with the old one below and a basket
 * button in the corner that carries the quantity once the product is in the
 * cart and then opens the cart drawer instead of adding another unit.
 */
const ProductCardCompactView = ({
  product,
  index,
  borderColor,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();
  const {
    currentItem,
    image,
    oldPrice,
    currentPrice,
    cartItemsCount,
    isOutOfStock,
    mixAndMatch,
    isFavorite,
    isBundle,
    changeCount,
    openSelectSKUModal,
  } = useProductCardV3(product, undefined, 'm');
  const { snippetRef, trackSnippetClick } = useProductSnippetAnalytics(
    currentItem,
    index,
  );
  const productList = useProductListAnalyticsContext();
  const mounted = useMounted();
  const choiceSkus = product.variants?.length
    ? product.variants
        .filter(({ value }) => value != null && value !== '')
        .map(({ sku }) => sku)
    : product.offers.map(({ sku }) => sku);
  const hasSkuChoice = new Set(choiceSkus).size > 1;
  const productLink = currentItem?.sku
    ? `/p/${product.slug}?sku=${encodeURIComponent(currentItem.sku)}`
    : `/p/${product.slug}`;

  const handleOpen = () => {
    trackSnippetClick();
    handleClickProduct(product)();
  };

  const handleCart = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isInCart) {
      if (company.hasCartDrawer) {
        cartDrawerStore.open(currentItem?.sku);
      } else {
        changeCount(cartItemsCount + 1, 'add');
      }
      return;
    }
    if (hasSkuChoice) {
      openSelectSKUModal();
      return;
    }
    changeCount(cartItemsCount + 1, 'add');
    if (
      company.hasCartDrawer &&
      currentItem &&
      cartStore.getCartItemCountById(currentItem) > cartItemsCount
    ) {
      cartDrawerStore.open(currentItem.sku);
    }
  };

  const handleFavorite = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    favoritesStore.toggleFavorite(
      product,
      productList?.source ?? 'product_main',
    );
    mainStore.sendToRN('hapticFeedback', { count: 1, type: 'soft' });
  };

  const isInCart = cartItemsCount > 0;
  const showFavorite =
    !company.hideFavoritesForGuests || (mounted && userStore.isAuthorized);
  const rating =
    product.ratingMarksCount > 0
      ? `${Number(product.ratingAverage).toFixed(1)} (${product.ratingMarksCount})`
      : '';
  const crossedPrice = mixAndMatch ? mixAndMatch.regularPrice : oldPrice;

  return (
    <div
      ref={snippetRef}
      style={borderColor ? { borderColor } : undefined}
      aria-disabled={disabled || undefined}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-[16px] border border-compact-stroke border-solid bg-white pb-3 text-left outline-action-brand! outline-offset-2 transition-colors duration-[120ms] ease-[cubic-bezier(.22,1,.36,1)] hover:bg-compact-card-hover has-[a:focus-visible]:outline-2! has-[a:focus-visible]:outline-solid!',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <span className="relative block aspect-square w-full">
        {image && (
          <CloudflareResponseImage
            src={image}
            srcParams={{ width: CARD_IMAGE_WIDTH }}
            srcSetParams={defaultSrcSetParams}
            sizes="(min-width: 1025px) 187px, 47vw"
            alt={product.name}
            loading="lazy"
            className={cn(
              'absolute inset-0 size-full object-contain p-[4px_4px_0]',
              isOutOfStock && 'opacity-50',
            )}
          />
        )}
        {isOutOfStock && (
          <span className="pointer-events-none absolute bottom-2 left-2 z-[2] rounded-full bg-white px-2 py-0.5 font-semibold text-[11px] text-text-primary leading-4 shadow-[0_1px_4px_rgba(20,20,21,0.12)]">
            {t('comingSoon')}
          </span>
        )}
        {showFavorite && (
          <button
            type="button"
            aria-label={
              isFavorite ? t('favoritesDE:remove') : t('favoritesDE:add')
            }
            onClick={handleFavorite}
            className={cn(
              'absolute top-1.5 right-1.5 z-[2] flex size-8 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 transition-colors duration-[120ms] ease-[cubic-bezier(.22,1,.36,1)]',
              isFavorite ? 'text-action-brand' : 'text-text-primary',
            )}
          >
            <HeartIcon
              filled={isFavorite}
              emptyFill="#fff"
              className="size-[22px]"
            />
          </button>
        )}
      </span>
      <span className="mt-auto block px-3 pt-2.5">
        <span className="mb-[5px] block h-4 overflow-hidden whitespace-nowrap font-medium text-[12px] text-compact-text-muted leading-4">
          {rating && (
            <>
              {company.compactCardRatingIcon === 'camel' ? (
                <img
                  src={camelRating}
                  alt=""
                  className="mr-1 inline-block h-[11px] w-auto align-[-1px]"
                />
              ) : (
                <StarIcon className="mr-0.5 inline-block size-3 align-[-2px] text-action-brand" />
              )}
              {rating}
            </>
          )}
        </span>
        <Link
          to={productLink}
          onClick={handleOpen}
          className="no-underline! line-clamp-2 h-8 overflow-hidden text-[12px] text-text-primary! leading-4 outline-none after:absolute after:inset-0 after:z-[1] after:content-['']"
        >
          {product.name}
        </Link>
        <span className="mt-[5px] flex items-center justify-between gap-2">
          <span className="flex h-8 min-w-0 flex-col">
            <span className="whitespace-nowrap font-semibold text-[14px] text-text-primary leading-[18px]">
              <PriceText>{mixAndMatch?.priceFrom ?? currentPrice}</PriceText>
            </span>
            {crossedPrice && (
              <span className="text-[12px] text-compact-text-muted leading-[14px] line-through">
                <PriceText>{crossedPrice}</PriceText>
              </span>
            )}
          </span>
          {isOutOfStock ? (
            !isBundle && (
              <NotifyBell productId={product.id} sku={currentItem?.sku ?? ''} />
            )
          ) : (
            <button
              type="button"
              aria-label={
                isInCart
                  ? t('cartDrawerDE:inCartCount', { count: cartItemsCount })
                  : t('addToCart')
              }
              onClick={handleCart}
              className={cn(
                'absolute right-3 bottom-3 z-[2] flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[8px] border-0 p-0 transition-colors duration-[120ms] ease-[cubic-bezier(.22,1,.36,1)]',
                isInCart
                  ? 'bg-action-brand text-white'
                  : 'bg-compact-control text-text-primary hover:bg-action-brand hover:text-white',
              )}
            >
              {isInCart && (
                <span className="pointer-events-none absolute -top-2 -right-2 box-border flex h-5 min-w-5 items-center justify-center rounded-[10px] border border-white border-solid bg-action-primary px-1 text-center font-semibold text-[10px] text-white leading-[18px]">
                  {cartItemsCount}
                </span>
              )}
              <CartSmallIcon className="size-4" />
            </button>
          )}
        </span>
      </span>
    </div>
  );
};

export const ProductCardCompact = observer(ProductCardCompactView);
